/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";

import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  Alert,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ProfileInfoCard from "examples/Cards/InfoCards/ProfileInfoCard";
import DefaultProjectCard from "examples/Cards/ProjectCards/DefaultProjectCard";

// Overview page components

import { Link } from "react-router-dom";
import Header from "layouts/profile/components/Header";
import PlatformSettings from "layouts/profile/components/PlatformSettings";
import PricingTierAssignment from "layouts/profile/components/PricingTierAssignment";
import PropertyLookupLimit from "layouts/profile/components/PropertyLookupLimit";
import UserOrders from "layouts/profile/components/UserOrders";
import AffiliateManagement from "layouts/profile/components/AffiliateManagement";
import { authenticatedFetch, authenticatedJsonRequest } from "services/api";
import { WHOLSTER_REST_BASE_URL } from "services/config";

// Images
import team1 from "assets/images/team-1.jpg";
import team2 from "assets/images/team-2.jpg";
import team3 from "assets/images/team-3.jpg";
import team4 from "assets/images/team-4.jpg";

function Overview() {
  const { id } = useParams();
  const [user, setUser] = useState({});
  const [listings, setListings] = useState([]);
  const [blocking, setBlocking] = useState([]);
  const [blockedBy, setBlockedBy] = useState([]);
  const [notificationPreferences, setNotificationPreferences] = useState([]);
  const [authorizations, setAuthorizations] = useState({});
  const [deltas, setDeltas] = useState({});
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState("");
  const [roleSaveError, setRoleSaveError] = useState("");
  const sessionId = useSelector((state) => state.login.sessionId);
  console.log("P", id);
  useEffect(() => {
    async function fetchData() {
      const response = await authenticatedFetch(
        `${WHOLSTER_REST_BASE_URL}/admin/user/${id}`,
        sessionId,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      const u = await response.json();
      setUser(u);
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchData() {
      const response = await authenticatedFetch(
        `${WHOLSTER_REST_BASE_URL}/admin/user/${id}/notificationPreferences`,
        sessionId,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      const p = await response.json();
      setNotificationPreferences(p);
    }
    fetchData();
  }, [id, sessionId]);

  useEffect(() => {
    async function fetchData() {
      const response = await authenticatedFetch(
        `${WHOLSTER_REST_BASE_URL}/admin/user/${id}/blocking`,
        sessionId,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      const p = await response.json();
      setBlocking(p);
    }
    fetchData();
  }, [id, sessionId]);

  useEffect(() => {
    async function fetchData() {
      console.log("lP", id);
      const response = await authenticatedFetch(
        `${WHOLSTER_REST_BASE_URL}/admin/user/${id}/blockedBy`,
        sessionId,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      const p = await response.json();
      setBlockedBy(p);
    }
    fetchData();
  }, [id, sessionId]);

  useEffect(() => {
    async function fetchData() {
      const response = await authenticatedFetch(
        `${WHOLSTER_REST_BASE_URL}/listing/seller/${id}`,
        sessionId,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      const u = await response.json();
      setListings(u);
    }
    fetchData();
  }, []);

  useEffect(() => {
    let active = true;
    setRolesLoading(true);
    setRolesError("");
    setRoleSaveError("");
    setAuthorizations({});
    setDeltas({});
    async function fetchData() {
      try {
        const auth = await authenticatedJsonRequest(
          `${WHOLSTER_REST_BASE_URL}/admin/user/${id}/authorizations`,
          sessionId
        );
        if (!active) return;
        setAuthorizations(auth.authorizations || {});
        setDeltas(auth.authorizations || {});
      } catch (error) {
        if (active) setRolesError(error.message || "Unable to load user roles.");
      } finally {
        if (active) setRolesLoading(false);
      }
    }
    fetchData();
    return () => {
      active = false;
    };
  }, [id, sessionId]);

  const handleAffiliateAssigned = () => {
    setAuthorizations((current) => ({ ...current, affiliate: true }));
    setDeltas((current) => ({ ...current, affiliate: true }));
  };

  const handleSaveAuthorizations = async () => {
    setRoleSaveError("");
    try {
      // Compare new authorizations with original to find changes
      const changes = {
        remove: [],
        add: []
      };

      Object.entries(authorizations).forEach(([key, originalValue]) => {
        const value = deltas?.[key];
        if (originalValue !== value) {
          if (value) {
            changes.add.push(key);
          } else {
            changes.remove.push(key);
          }
        }
      });
      //Adding comments
      await authenticatedJsonRequest(
        `${WHOLSTER_REST_BASE_URL}/admin/user/${id}/authorizations`,
        sessionId,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(changes)
        }
      );
      setAuthorizations({ ...deltas });
    } catch (error) {
      setRoleSaveError(error.message || "Unable to save user roles.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header user={user}>
        <MDBox mt={5} mb={3}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6} xl={4}>
              <PlatformSettings
                userId={user.id}
                username={user.username}
                membership={user.membership}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={4} sx={{ display: "flex" }}>
              <Divider orientation="vertical" sx={{ ml: -2, mr: 1 }} />
              <ProfileInfoCard
                title="profile information"
                description={user.bio || ""}
                info={{
                  fullName: `${user.firstName} ${user.lastName}`,
                  mobile: `(${user?.phoneNumber?.areaCode || "xxx"}) ${
                    user?.phoneNumber?.exchange || "xxx"
                  }-${user?.phoneNumber?.extension || "xxxx"}`,
                  email: user.username,
                  website: user.website,
                  location: "USA",
                }}
                social={[]}
                action={{ route: "", tooltip: "Edit Profile" }}
                shadow={false}
              />
              <Divider orientation="vertical" sx={{ mx: 0 }} />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <PricingTierAssignment userId={id} />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <PropertyLookupLimit userId={id} />
            </Grid>
          </Grid>
          {rolesError && <Alert severity="error" sx={{ mt: 3 }}>{rolesError}</Alert>}
          {!rolesLoading && !rolesError && (
            <AffiliateManagement
              key={`${id}:${sessionId}`}
              userId={id}
              isAffiliate={authorizations.affiliate === true}
              onAssigned={handleAffiliateAssigned}
            />
          )}
          <UserOrders userId={id} />
          <Grid item xs={12} md={6} xl={4}>
            <MDTypography variant="h6" fontWeight="medium">
              Notification Preferences
            </MDTypography>
            <MDBox pt={2} px={2} lineHeight={1.25}>
              <List sx={{ fontWeight: "medium", fontSize: ".75em" }}>
                {notificationPreferences.map((n) => (
                  <ListItem key={n.name}>
                    <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                      {n.optedOut ? (
                        <CheckBoxOutlineBlankIcon
                          fontSize="small"
                          color="inherit"
                        />
                      ) : (
                        <CheckBoxIcon fontSize="small" color="inherit" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: "1em" }}>
                          {n.name}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <MDTypography variant="h6" fontWeight="medium">
              Blocked By
            </MDTypography>
            <MDBox pt={2} px={2} lineHeight={1.25}>
              <List sx={{ fontWeight: "medium", fontSize: ".75em" }}>
                {blockedBy.map((n) => (
                  <ListItem key={n.name}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: "1em" }}>
                        <Link to={"/profile/" + n.id}>
                          {n.firstName} {n.lastName}
                        </Link>
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <MDTypography variant="h6" fontWeight="medium">
              Blocking
            </MDTypography>
            <MDBox pt={2} px={2} lineHeight={1.25}>
              <List sx={{ fontWeight: "medium", fontSize: ".75em" }}>
                {blocking.map((n) => (
                  <ListItem key={n.name}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: "1em" }}>
                          <Link to={"/profile/" + n.id}>
                            {n.firstName} {n.lastName}
                          </Link>
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6" fontWeight="medium">
                Roles
              </MDTypography>
              <MDTypography
                component="button"
                variant="button"
                color="info"
                onClick={handleSaveAuthorizations}
                disabled={rolesLoading || Boolean(rolesError)}
                sx={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.75 }
                }}
              >
                Save Changes
              </MDTypography>
            </MDBox>
            {roleSaveError && <Alert severity="error">{roleSaveError}</Alert>}
            <MDBox pt={2} px={2} lineHeight={1.25}>
              <List sx={{ fontWeight: "medium", fontSize: ".75em" }}>
                {Object.entries(deltas).map(([key, value]) => (
                  <ListItem key={key}>
                    <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                      <div 
                        onClick={() => setDeltas(prev => ({
                          ...prev,
                          [key]: !value
                        }))}
                        style={{ cursor: 'pointer' }}
                      >
                        {value ? (
                          <CheckBoxIcon fontSize="small" color="inherit" />
                        ) : (
                          <CheckBoxOutlineBlankIcon fontSize="small" color="inherit" />
                        )}
                      </div>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: "1em" }}>
                          {key}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </MDBox>
          </Grid>
        </MDBox>
        <MDBox pt={2} px={2} lineHeight={1.25}>
          <MDTypography variant="h6" fontWeight="medium">
            Listings
          </MDTypography>
          <MDBox mb={1}>
            <MDTypography variant="button" color="text">
              Listings for {user.firstName || ""}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={6}>
            {listings.map((listing) => (
              <Grid item xs={12} md={6} xl={3}>
                <DefaultProjectCard
                  image={`${WHOLSTER_REST_BASE_URL}/images/listing/${listing.identifierKey}`}
                  label={`${listing.details.address.houseNumber} ${listing.details.address.street}`}
                  title={`$${Number(listing.details.cost).toLocaleString(
                    "en"
                  )}`}
                  description={listing.details.details}
                  action={{
                    type: "external",
                    route: `https://www.wholstersystems.com/listing/${listing.id}`,
                    color: "info",
                    label: "view listing",
                  }}
                  authors={[
                    { image: team1, name: "Elena Morison" },
                    { image: team2, name: "Ryan Milly" },
                    { image: team3, name: "Nick Daniel" },
                    { image: team4, name: "Peterson" },
                  ]}
                />
              </Grid>
            ))}
          </Grid>
        </MDBox>
      </Header>
    </DashboardLayout>
  );
}

export default Overview;
